import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  appendLcUserStationParams,
  lcApiBase,
  lcHttpError,
  lcStationId,
  requireLcUserStationIds,
} from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';
import { ProduitItem, ProduitsPageResult } from './logiciel-caisse-produit.service';

export interface StockLigneParEntrepot {
  entrepot_id: number | null;
  entrepot_libelle: string;
  stock_physique: number;
  stock_disponible: number;
}

interface StockDetailResponse {
  success: boolean;
  lignes?: StockLigneParEntrepot[];
  error?: string;
}

interface GestionGlobaleResponse {
  success: boolean;
  produits?: ProduitItem[];
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    pages?: number;
  };
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseStockGestionService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  getGestionGlobale(options: {
    actif: boolean;
    page?: number;
    limit?: number;
    byGroup?: boolean;
    entrepotIds?: number[];
    searchRef?: string;
    stockPhysMin?: number;
    stockPhysMax?: number;
  }): Observable<ProduitsPageResult> {
    try {
      requireLcUserStationIds(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }

    const page = options.page ?? 1;
    const limit = options.limit ?? 10;
    let params = new HttpParams()
      .set('actif', String(options.actif))
      .set('page', String(page))
      .set('limit', String(limit))
      .set('by_group', options.byGroup !== false ? 'true' : 'false');

    const ids = (options.entrepotIds ?? []).filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length > 0) {
      params = params.set('entrepot_ids', ids.join(','));
    }

    const q = String(options.searchRef ?? '').trim();
    if (q.length > 0) {
      params = params.set('q', q.slice(0, 120));
    }

    if (options.stockPhysMin != null && Number.isFinite(options.stockPhysMin)) {
      params = params.set('stock_phys_min', String(options.stockPhysMin));
    }
    if (options.stockPhysMax != null && Number.isFinite(options.stockPhysMax)) {
      params = params.set('stock_phys_max', String(options.stockPhysMax));
    }

    params = appendLcUserStationParams(params, this.authService);

    const url = `${lcApiBase(this.authService)}/api/stocks/gestion-globale`;
    return this.http.get<GestionGlobaleResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.produits)) {
          throw new Error(res?.error || 'Réponse gestion stock invalide');
        }
        const m = res.meta ?? {};
        const total = m.total ?? res.produits.length;
        const lim = m.limit ?? limit;
        const pg = m.page ?? page;
        const pages = m.pages ?? Math.max(1, Math.ceil(total / lim));
        return {
          items: res.produits,
          pagination: {
            page: pg,
            limit: lim,
            total,
            totalPages: pages,
          },
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement du stock global.'))
      )
    );
  }

  getStockDetailParEntrepot(
    produitIds: number[],
    entrepotIds?: number[]
  ): Observable<StockLigneParEntrepot[]> {
    try {
      requireLcUserStationIds(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const uniq = [
      ...new Set(
        produitIds.filter((n) => Number.isFinite(n) && n > 0).map((n) => Math.floor(n))
      ),
    ];
    if (uniq.length === 0) {
      return throwError(() => new Error('Aucun produit valide.'));
    }
    let params = new HttpParams().set('produit_ids', uniq.join(','));
    const ent = (entrepotIds ?? []).filter((n) => Number.isFinite(n) && n > 0);
    if (ent.length > 0) {
      params = params.set('entrepot_ids', ent.join(','));
    }
    params = appendLcUserStationParams(params, this.authService);
    const url = `${lcApiBase(this.authService)}/api/stocks/gestion-globale/detail`;
    return this.http.get<StockDetailResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.lignes)) {
          throw new Error(res?.error || 'Réponse détail stock invalide');
        }
        return res.lignes;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement du détail stock.'))
      )
    );
  }

  /**
   * GET /api/stocks/stock/produit/:user_id/:station_id/:produits_id
   * Quantité stock physique (somme entrepôts). `code_barre` optionnel pour résoudre l’id caisse.
   */
  getStockPhysiqueByProduit(
    produitsId: number,
    options?: { code_barre?: string | null }
  ): Observable<number> {
    const userId = this.authService.getBackUserId();
    const stationId = this.authService.getBackStationId();
    if (userId == null || stationId == null) {
      return throwError(
        () => new Error('Session caisse incomplète (user/station). Reconnectez le module logiciel caisse.')
      );
    }
    const pid = Number(produitsId);
    if (!Number.isFinite(pid) || pid <= 0) {
      return of(0);
    }
    let params = new HttpParams();
    const cb = options?.code_barre != null ? String(options.code_barre).replace(/\s/g, '').trim() : '';
    if (cb && cb !== '0' && cb !== '-') {
      params = params.set('code_barre', cb);
    }
    const url = `${lcApiBase(this.authService)}/api/stocks/stock/produit/${userId}/${stationId}/${pid}`;
    return this.http
      .get<{ success?: boolean; stock?: { stock_physique?: number } | null }>(url, { params })
      .pipe(
        map((res) => {
          const n = Number(res?.stock?.stock_physique ?? 0);
          return Number.isFinite(n) ? n : 0;
        }),
        catchError(() => of(0))
      );
  }

  private cleanBarcodeKey(value: unknown): string {
    const s = value != null ? String(value).replace(/\s/g, '').trim() : '';
    if (!s || s === '0' || s === '-' || s === '—') {
      return '';
    }
    return s;
  }

  private cleanRefKey(value: unknown): string {
    const s = value != null ? String(value).trim().toLowerCase() : '';
    if (!s || s === '-' || s === '—' || s === 'null') {
      return '';
    }
    return s;
  }

  private toStockPair(physique: unknown, disponible: unknown): { physique: number; disponible: number } {
    const sp = Number(physique);
    const sd = Number(disponible);
    return {
      physique: Number.isFinite(sp) ? sp : 0,
      disponible: Number.isFinite(sd) ? sd : 0,
    };
  }

  private addStockPair(
    a: { physique: number; disponible: number },
    b: { physique: number; disponible: number }
  ): { physique: number; disponible: number } {
    return {
      physique: a.physique + b.physique,
      disponible: a.disponible + b.disponible,
    };
  }

  /**
   * Index STOCK PHYSIQUE + STOCK DISPONIBLE (gestion-globale).
   */
  private loadGestionGlobaleStockIndex(
    actif: boolean,
    entrepotIds?: number[]
  ): Observable<{
    byBarcode: Map<string, { physique: number; disponible: number }>;
    byRefInterne: Map<string, { physique: number; disponible: number }>;
  }> {
    const pageSize = 100;
    const byBarcode = new Map<string, { physique: number; disponible: number }>();
    const byRefInterne = new Map<string, { physique: number; disponible: number }>();
    const ids = (entrepotIds || []).filter((n) => Number.isFinite(n) && n > 0);

    const ingest = (items: ProduitItem[]) => {
      for (const p of items || []) {
        const pair = this.toStockPair(p.stock_physique_global, p.stock_disponible_global);
        const cb = this.cleanBarcodeKey(p.code_barre);
        if (cb) {
          byBarcode.set(cb, pair);
        }
        const refInt = this.cleanRefKey(p.reference_interne);
        if (refInt) {
          const prev = byRefInterne.get(refInt) ?? { physique: 0, disponible: 0 };
          byRefInterne.set(refInt, this.addStockPair(prev, pair));
        }
      }
    };

    const loadPage = (
      page: number
    ): Observable<{
      byBarcode: Map<string, { physique: number; disponible: number }>;
      byRefInterne: Map<string, { physique: number; disponible: number }>;
    }> =>
      this.getGestionGlobale({
        actif,
        page,
        limit: pageSize,
        byGroup: true,
        entrepotIds: ids.length ? ids : undefined,
      }).pipe(
        switchMap((res) => {
          ingest(res.items || []);
          const totalPages = Math.max(1, Number(res.pagination?.totalPages) || 1);
          if (page < totalPages && page < 80) {
            return loadPage(page + 1);
          }
          return of({ byBarcode, byRefInterne });
        })
      );

    return loadPage(1);
  }

  private pickStockPairFromGestionItems(
    items: ProduitItem[],
    opts: { code_barre?: string | null; ref_interne?: string | null }
  ): { physique: number; disponible: number } | null {
    const cb = this.cleanBarcodeKey(opts.code_barre);
    if (cb) {
      const hit = (items || []).find((p) => this.cleanBarcodeKey(p.code_barre) === cb);
      if (hit) {
        return this.toStockPair(hit.stock_physique_global, hit.stock_disponible_global);
      }
      return null;
    }
    const refInt = this.cleanRefKey(opts.ref_interne);
    if (refInt) {
      const matches = (items || []).filter(
        (p) => this.cleanRefKey(p.reference_interne) === refInt
      );
      if (!matches.length) {
        return null;
      }
      return matches.reduce(
        (acc, p) =>
          this.addStockPair(
            acc,
            this.toStockPair(p.stock_physique_global, p.stock_disponible_global)
          ),
        { physique: 0, disponible: 0 }
      );
    }
    return null;
  }

  /**
   * STOCK PHYSIQUE + STOCK DISPONIBLE (même API que Gestion stock).
   * Appariement ERP ↔ caisse : code-barres, sinon réf. interne.
   */
  getStockGlobaleByErpProducts(
    products: Array<{
      id: number;
      code_barre?: string | null;
      ref_interne?: string | null;
      ref_fabricant?: string | null;
    }>,
    options?: { actif?: boolean; entrepotIds?: number[] }
  ): Observable<Record<number, { physique: number; disponible: number }>> {
    const list = (products || [])
      .map((p) => ({
        id: Number(p.id),
        code_barre: p.code_barre,
        ref_interne: p.ref_interne,
        ref_fabricant: p.ref_fabricant,
      }))
      .filter((p) => Number.isFinite(p.id) && p.id > 0);
    if (!list.length) {
      return of({});
    }
    const actif = options?.actif !== false;
    const entrepotIds = options?.entrepotIds;
    return this.authService.ensureAuthenticatedSession().pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(() => new Error('Session logiciel caisse indisponible.'));
        }
        return this.loadGestionGlobaleStockIndex(actif, entrepotIds);
      }),
      switchMap((index) => {
        const out: Record<number, { physique: number; disponible: number }> = {};
        const missing: typeof list = [];
        for (const p of list) {
          const cb = this.cleanBarcodeKey(p.code_barre);
          if (cb && index.byBarcode.has(cb)) {
            out[p.id] = index.byBarcode.get(cb)!;
            continue;
          }
          const refInt = this.cleanRefKey(p.ref_interne);
          if (!cb && refInt && index.byRefInterne.has(refInt)) {
            out[p.id] = index.byRefInterne.get(refInt)!;
            continue;
          }
          if (!cb && !refInt) {
            out[p.id] = { physique: 0, disponible: 0 };
            continue;
          }
          missing.push(p);
        }
        if (!missing.length) {
          return of(out);
        }
        return forkJoin(
          missing.map((p) => {
            const q =
              this.cleanBarcodeKey(p.code_barre) ||
              (p.ref_interne != null ? String(p.ref_interne).trim() : '');
            if (!q) {
              return of({ id: p.id, pair: { physique: 0, disponible: 0 } });
            }
            return this.getGestionGlobale({
              actif,
              page: 1,
              limit: 100,
              byGroup: true,
              searchRef: q.slice(0, 120),
              entrepotIds,
            }).pipe(
              map((res) => {
                const picked = this.pickStockPairFromGestionItems(res.items || [], {
                  code_barre: p.code_barre,
                  ref_interne: p.ref_interne,
                });
                return { id: p.id, pair: picked ?? { physique: 0, disponible: 0 } };
              }),
              catchError(() => of({ id: p.id, pair: { physique: 0, disponible: 0 } }))
            );
          })
        ).pipe(
          map((rows) => {
            for (const r of rows) {
              out[r.id] = r.pair;
            }
            return out;
          })
        );
      }),
      catchError(() => {
        const empty: Record<number, { physique: number; disponible: number }> = {};
        for (const p of list) {
          empty[p.id] = { physique: 0, disponible: 0 };
        }
        return of(empty);
      })
    );
  }

  /**
   * Totaux stock physique (ÉTAT EN STOCK / badge documents) — même source que STOCK PHYSIQUE.
   */
  getStockPhysiqueByErpProducts(
    products: Array<{
      id: number;
      code_barre?: string | null;
      ref_interne?: string | null;
      ref_fabricant?: string | null;
    }>,
    options?: { actif?: boolean; entrepotIds?: number[] }
  ): Observable<Record<number, number>> {
    return this.getStockGlobaleByErpProducts(products, options).pipe(
      map((rec) => {
        const out: Record<number, number> = {};
        for (const [k, v] of Object.entries(rec)) {
          out[Number(k)] = v?.physique ?? 0;
        }
        return out;
      })
    );
  }
}
